import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import {
  Wrench,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Settings,
  Loader2,
  Database,
  Activity,
  Zap,
  Link2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DataFixToolbar - A reusable component that provides data fix functionality
 * Can be added to any page to enable users to fix data inconsistencies
 * 
 * Props:
 * - onDataFixed: Callback function to refresh page data after a fix
 * - showQuickFix: Show quick census fix buttons (default: true)
 * - showFullFix: Show full data fix dialog (default: true)
 * - showSyncButton: Show data sync button (default: true)
 * - variant: 'full' | 'compact' | 'minimal' (default: 'compact')
 * - className: Additional CSS classes
 */
export default function DataFixToolbar({
  onDataFixed,
  showQuickFix = true,
  showFullFix = true,
  showSyncButton = true,
  variant = 'compact',
  className = '',
}) {
  const [showFixDialog, setShowFixDialog] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [quickFixing, setQuickFixing] = useState(false);
  
  const [fixValues, setFixValues] = useState({
    current_census: 197,
    monthly_revenue: 485000,
    weekly_admissions: 18,
    weekly_discharges: 15,
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('qapi-token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  // Quick fix census with a specific value
  const handleQuickFixCensus = async (censusValue) => {
    setQuickFixing(true);
    try {
      const response = await fetch(`${API_URL}/api/data-sync/fix-census`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          current_census: censusValue,
          admissions: 18,
          discharges: 15,
        }),
      });

      if (response.ok) {
        toast.success(`Census fixed to ${censusValue}`, {
          description: 'Data synced across all collections',
        });
        onDataFixed?.();
      } else {
        const error = await response.json();
        toast.error('Fix failed', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error fixing census');
    } finally {
      setQuickFixing(false);
    }
  };

  // Full data fix with custom values
  const handleFixAllData = async () => {
    setFixing(true);
    try {
      const response = await fetch(`${API_URL}/api/data-sync/fix-all-inconsistencies`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(fixValues),
      });

      if (response.ok) {
        toast.success('Data fixed and synced successfully', {
          description: 'All collections updated with correct values',
        });
        setShowFixDialog(false);
        onDataFixed?.();
      } else {
        const error = await response.json();
        toast.error('Fix failed', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error fixing data');
    } finally {
      setFixing(false);
    }
  };

  // Trigger full data sync
  const handleFullSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/api/data-sync/full-sync`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Data synchronized', {
          description: `${result.results?.total_synced || 0} records synced across all collections`,
        });
        onDataFixed?.();
      } else {
        toast.error('Sync failed');
      }
    } catch (error) {
      toast.error('Error syncing data');
    } finally {
      setSyncing(false);
    }
  };

  // Minimal variant - just a small icon button
  if (variant === 'minimal') {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 ${className}`}
            data-testid="data-fix-minimal-btn"
          >
            <Wrench className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64" align="end">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-violet-500" />
              <span className="font-medium text-sm">Data Tools</span>
            </div>
            <div className="flex flex-col gap-2">
              {showSyncButton && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleFullSync}
                  disabled={syncing}
                  className="justify-start gap-2"
                >
                  {syncing ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3" />
                  )}
                  Sync Data
                </Button>
              )}
              {showFullFix && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowFixDialog(true)}
                  className="justify-start gap-2"
                >
                  <Wrench className="h-3 w-3" />
                  Fix Data
                </Button>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // Compact variant - inline buttons
  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`} data-testid="data-fix-toolbar">
        {showSyncButton && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleFullSync}
            disabled={syncing}
            className="gap-1.5"
            data-testid="sync-data-btn"
          >
            {syncing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Link2 className="h-3.5 w-3.5" />
            )}
            Sync
          </Button>
        )}
        
        {showQuickFix && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={quickFixing}
                data-testid="quick-fix-btn"
              >
                {quickFixing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Zap className="h-3.5 w-3.5" />
                )}
                Quick Fix
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56" align="end">
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium">Set census to:</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {[178, 185, 190, 195, 197, 200].map((val) => (
                    <Button
                      key={val}
                      variant="secondary"
                      size="sm"
                      onClick={() => handleQuickFixCensus(val)}
                      disabled={quickFixing}
                      className="text-xs"
                    >
                      {val}
                    </Button>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}
        
        {showFullFix && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFixDialog(true)}
            className="gap-1.5"
            data-testid="fix-data-btn"
          >
            <Wrench className="h-3.5 w-3.5" />
            Fix Data
          </Button>
        )}
        
        {/* Full Fix Dialog */}
        <Dialog open={showFixDialog} onOpenChange={setShowFixDialog}>
          <DialogContent className="max-w-md bg-slate-900 border-slate-700">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white font-bold">
                <Settings className="h-5 w-5 text-cyan-400" />
                Fix Data Inconsistencies
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Enter the correct values to sync across all collections.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="census" className="text-slate-300 font-bold">
                  Current Census (ADC)
                </Label>
                <Input
                  id="census"
                  type="number"
                  value={fixValues.current_census}
                  onChange={(e) =>
                    setFixValues({
                      ...fixValues,
                      current_census: parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="e.g., 197"
                  className="bg-slate-800 border-slate-600 text-white"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="revenue" className="text-slate-300 font-bold">
                  Monthly Revenue ($)
                </Label>
                <Input
                  id="revenue"
                  type="number"
                  value={fixValues.monthly_revenue}
                  onChange={(e) =>
                    setFixValues({
                      ...fixValues,
                      monthly_revenue: parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="e.g., 485000"
                  className="bg-slate-800 border-slate-600 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label className="text-slate-300 font-bold">Weekly Admissions</Label>
                  <Input
                    type="number"
                    value={fixValues.weekly_admissions}
                    onChange={(e) =>
                      setFixValues({
                        ...fixValues,
                        weekly_admissions: parseInt(e.target.value) || 0,
                      })
                    }
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-slate-300 font-bold">Weekly Exits</Label>
                  <Input
                    type="number"
                    value={fixValues.weekly_discharges}
                    onChange={(e) =>
                      setFixValues({
                        ...fixValues,
                        weekly_discharges: parseInt(e.target.value) || 0,
                      })
                    }
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                </div>
              </div>
              <Alert className="bg-amber-900/20 border-amber-600/30">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                <AlertDescription className="text-amber-200 text-sm">
                  This will update census history and financial data based on these values.
                </AlertDescription>
              </Alert>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowFixDialog(false)}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                onClick={handleFixAllData}
                disabled={fixing}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500"
              >
                {fixing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Wrench className="h-4 w-4 mr-2" />
                )}
                Fix All Data
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Full variant - expanded with all options visible
  return (
    <div
      className={`p-4 rounded-xl bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 ${className}`}
      data-testid="data-fix-toolbar-full"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-500/20 rounded-xl">
            <Database
              className="h-5 w-5 text-violet-400"
              style={{ filter: 'drop-shadow(0 0 5px rgba(167, 139, 250, 0.5))' }}
            />
          </div>
          <div>
            <h4 className="font-bold text-white">Data Fix Tools</h4>
            <p className="text-sm text-slate-400">
              Fix inconsistencies and sync data across all modules
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {showSyncButton && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleFullSync}
              disabled={syncing}
              className="gap-2 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
              data-testid="sync-all-btn"
            >
              {syncing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Sync All Data
            </Button>
          )}

          {showQuickFix && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-400 mr-1">Quick Fix:</span>
              {[185, 190, 195, 197, 200].map((val) => (
                <Button
                  key={val}
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickFixCensus(val)}
                  disabled={quickFixing}
                  className="h-7 px-2 text-xs border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  {val}
                </Button>
              ))}
            </div>
          )}

          {showFullFix && (
            <Dialog open={showFixDialog} onOpenChange={setShowFixDialog}>
              <DialogTrigger asChild>
                <Button
                  className="gap-2 bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-400 hover:to-red-500 border-0"
                  data-testid="open-fix-dialog-btn"
                >
                  <Wrench className="h-4 w-4" />
                  Fix Data
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md bg-slate-900 border-slate-700">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-white font-bold">
                    <Settings className="h-5 w-5 text-cyan-400" />
                    Fix Data Inconsistencies
                  </DialogTitle>
                  <DialogDescription className="text-slate-400">
                    Enter the correct values to sync across all collections.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="census-full" className="text-slate-300 font-bold">
                      Current Census (ADC)
                    </Label>
                    <Input
                      id="census-full"
                      type="number"
                      value={fixValues.current_census}
                      onChange={(e) =>
                        setFixValues({
                          ...fixValues,
                          current_census: parseInt(e.target.value) || 0,
                        })
                      }
                      placeholder="e.g., 197"
                      className="bg-slate-800 border-slate-600 text-white"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="revenue-full" className="text-slate-300 font-bold">
                      Monthly Revenue ($)
                    </Label>
                    <Input
                      id="revenue-full"
                      type="number"
                      value={fixValues.monthly_revenue}
                      onChange={(e) =>
                        setFixValues({
                          ...fixValues,
                          monthly_revenue: parseInt(e.target.value) || 0,
                        })
                      }
                      placeholder="e.g., 485000"
                      className="bg-slate-800 border-slate-600 text-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label className="text-slate-300 font-bold">Weekly Admissions</Label>
                      <Input
                        type="number"
                        value={fixValues.weekly_admissions}
                        onChange={(e) =>
                          setFixValues({
                            ...fixValues,
                            weekly_admissions: parseInt(e.target.value) || 0,
                          })
                        }
                        className="bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label className="text-slate-300 font-bold">Weekly Exits</Label>
                      <Input
                        type="number"
                        value={fixValues.weekly_discharges}
                        onChange={(e) =>
                          setFixValues({
                            ...fixValues,
                            weekly_discharges: parseInt(e.target.value) || 0,
                          })
                        }
                        className="bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                  <Alert className="bg-amber-900/20 border-amber-600/30">
                    <AlertTriangle className="h-4 w-4 text-amber-400" />
                    <AlertDescription className="text-amber-200 text-sm">
                      This will update census history and financial data.
                    </AlertDescription>
                  </Alert>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setShowFixDialog(false)}
                    className="border-slate-600 text-slate-300 hover:bg-slate-800"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleFixAllData}
                    disabled={fixing}
                    className="bg-gradient-to-r from-cyan-500 to-blue-600"
                  >
                    {fixing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Wrench className="h-4 w-4 mr-2" />
                    )}
                    Fix All Data
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </div>
  );
}

// Export named component for direct imports
export { DataFixToolbar };
